import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomInput, LoadingOverlay, useAlert } from '../../components';
import { signUpWithPhone, signInWithPhone } from '../../services/authService';
import './LoginScreen.css';

// Simple Captcha Generator
const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid ambiguous chars
    let captcha = '';
    for (let i = 0; i < 6; i++) {
        captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return captcha;
};

export const LoginScreen = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const alert = useAlert();
    const [isSignUp, setIsSignUp] = useState(false);

    // Form fields
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [captcha, setCaptcha] = useState('');
    const [userCaptcha, setUserCaptcha] = useState('');
    const [isReferralFromUrl, setIsReferralFromUrl] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Initialize captcha
    useEffect(() => {
        setCaptcha(generateCaptcha());
    }, []);

    // Check for referral code in URL
    useEffect(() => {
        const refCode = searchParams.get('ref') || searchParams.get('referral');
        if (refCode) {
            setReferralCode(refCode.toUpperCase());
            setIsReferralFromUrl(true);
            setIsSignUp(true);
        }
    }, [searchParams]);

    const refreshCaptcha = () => {
        setCaptcha(generateCaptcha());
        setUserCaptcha('');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!phone || phone.length < 10) {
            newErrors.phone = 'Enter a valid 10-digit mobile number';
        }

        if (!password || password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (isSignUp) {
            if (password !== confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
            if (!referralCode || referralCode.trim() === '') {
                newErrors.referralCode = 'Invitation code is required';
            }
            // Only validate captcha for signup
            if (userCaptcha.toUpperCase() !== captcha) {
                newErrors.captcha = 'Invalid captcha code';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            if (isSignUp) {
                await signUpWithPhone(phone, password, referralCode);
                alert.showSuccess('Success', 'Account created successfully!');
            } else {
                await signInWithPhone(phone, password);
            }
            navigate('/');
        } catch (error) {
            console.error('Auth error:', error);
            let msg = 'Authentication failed. Please check your credentials.';
            if (error.message && error.message.includes('blocked')) {
                msg = 'Your account has been blocked. Please contact support.';
            } else if (error.message && error.message.includes('already registered')) {
                msg = 'Mobile number already registered. Please login instead.';
            } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                msg = 'Invalid mobile number or password';
            } else if (error.code === 'auth/email-already-in-use') {
                msg = 'Mobile number already registered';
            } else if (error.message && error.message.includes('Invalid referral code')) {
                msg = 'Invalid referral code. Please check and try again.';
            }
            alert.showError('Error', msg);
            // Only refresh captcha for signup
            if (isSignUp) {
                refreshCaptcha();
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen">
            <LoadingOverlay visible={loading} message={isSignUp ? 'Creating account...' : 'Signing in...'} />

            <div className="login-container">
                {/* Logo Section */}
                <div className="auth-logo-section">
                    <h1 className="auth-brand-name">NEXTRON</h1>
                    <p className="auth-tagline">
                        {isSignUp ? 'DIGITAL POWER EVOLUTION' : 'Welcome back to future investing'}
                    </p>
                </div>

                {/* Auth Card */}
                <div className="auth-form-card">
                    {isSignUp && <h2 className="auth-form-title">CREATE ACCOUNT</h2>}

                    <form onSubmit={handleSubmit}>
                        {/* Mobile Number Field */}
                        <div className="auth-input-group">
                            <label className="auth-label">{isSignUp ? 'REGISTER NUMBER' : 'Mobile Number'}</label>
                            <CustomInput
                                type="tel"
                                placeholder={isSignUp ? 'Mobile Number' : 'Enter mobile number'}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                leftIcon="phone"
                                error={errors.phone}
                            />
                        </div>

                        {/* Password Fields */}
                        {isSignUp ? (
                            <>
                                <div className="auth-input-group">
                                    <label className="auth-label">PASSWORD</label>
                                    <CustomInput
                                        placeholder="Pin"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        isPassword
                                        error={errors.password}
                                    />
                                </div>
                                <div className="auth-input-group">
                                    <label className="auth-label">CONFIRM PASSWORD</label>
                                    <CustomInput
                                        placeholder="Confirm Pin"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        isPassword
                                        error={errors.confirmPassword}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="auth-input-group">
                                <label className="auth-label">Password</label>
                                <CustomInput
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    leftIcon="lock"
                                    isPassword
                                    error={errors.password}
                                />
                            </div>
                        )}

                        {isSignUp && (
                            <>
                                {/* Invitation Code */}
                                <div className="auth-input-group">
                                    <label className="auth-label">INVITATION CODE</label>
                                    <CustomInput
                                        placeholder="Referral Code"
                                        value={referralCode}
                                        onChange={(e) => !isReferralFromUrl && setReferralCode(e.target.value.toUpperCase())}
                                        leftIcon="tag"
                                        disabled={isReferralFromUrl}
                                        error={errors.referralCode}
                                    />
                                </div>

                                {/* Captcha Section */}
                                <div className="auth-input-group">
                                    <label className="auth-label">HUMAN VERIFICATION</label>
                                    <div className="captcha-row">
                                        <CustomInput
                                            placeholder="Enter Code"
                                            value={userCaptcha}
                                            onChange={(e) => setUserCaptcha(e.target.value)}
                                            error={errors.captcha}
                                        />
                                        <div className="captcha-code-display">{captcha}</div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Submit Button */}
                        <button type="submit" className="auth-submit-btn" disabled={loading}>
                            {isSignUp && <span className="material-icons">person_add</span>}
                            {isSignUp ? 'REGISTER NOW' : 'LOGIN'}
                        </button>
                    </form>

                    {/* Switch Mode */}
                    <p className="auth-switch-text">
                        {isSignUp ? 'ALREADY HAVE AN ACCOUNT? ' : "Don't have an account? "}
                        <button
                            type="button"
                            className="auth-switch-btn"
                            onClick={() => {
                                setIsSignUp(!isSignUp);
                                setErrors({});
                                if (!isSignUp) {
                                    refreshCaptcha();
                                }
                            }}
                        >
                            {isSignUp ? 'LOGIN NOW' : 'Register Now'}
                        </button>
                    </p>

                    {/* Terms Text */}
                    {isSignUp && (
                        <p className="auth-terms-text">
                            BY REGISTERING, YOU AGREE TO OUR TERMS OF SERVICE AND INVESTMENT DISCLOSURE POLICIES.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
